import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

/**
 * WorkflowManagementGridPage'in tam-sayfa karşılığı — workflow başlık kaydının
 * (Ad/Açıklama/Durum) /workflows/new ve /workflows/:id detay ekranı.
 * Saha (input) tanımları alt-grid'i Section + SubModal ile yönetilir; aynı
 * backend uçlarını kullanır (/api/workflows + /{id}/inputs).
 *
 * Saha tanımları yalnızca kayıt mevcutken (edit modu) düzenlenebilir; yeni
 * kayıtta önce workflow kaydedilir (PersonnelDetailPage izin sekmesi deseni).
 */
const API_BASE = '/api/workflows';

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Pasif' },
];

const DATA_TYPE_OPTIONS = [
  { value: '1', label: 'String' },
  { value: '2', label: 'Number' },
  { value: '3', label: 'Decimal' },
  { value: '4', label: 'Boolean' },
  { value: '5', label: 'Date' },
];

const EMPTY_FORM = { name: '', description: '', isActive: 'true' };
const EMPTY_INPUT = { fieldName: '', dataType: '1', description: '', defaultValue: '', isRequired: false, isActive: true };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function getDataTypeLabel(value) {
  return DATA_TYPE_OPTIONS.find((o) => o.value === String(value))?.label || '-';
}
function normalizeFieldName(value) { return (value || '').trim().toLocaleLowerCase('tr-TR'); }

export function WorkflowDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Workflow' : 'Workflow Detayı', 'Workflow ve saha tanımlarını yönetin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Saha (input) tanımları alt-grid
  const [inputs, setInputs] = useState([]);
  const [inputsLoading, setInputsLoading] = useState(false);
  const [inputModal, setInputModal] = useState(null); // { id|null }
  const [inputForm, setInputForm] = useState(EMPTY_INPUT);
  const [inputSaving, setInputSaving] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setInput = (k, v) => setInputForm((c) => ({ ...c, [k]: v }));

  const rowToForm = useCallback((r) => ({
    name: pick(r, 'name', 'Name') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    isActive: (pick(r, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
  }), []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      setForm(rowToForm(location.state.row));
      // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
      // Formu anında doldur; audit'i tam kayıttan (REST /{id}) NON-BLOCKING çek.
      fetch(`${API_BASE}/${id}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let r = null;
      try {
        const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
        if (res.ok) r = unwrap(await res.json());
      } catch { /* listeye düş */ }
      if (!r) {
        const res = await fetch(`${API_BASE}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Workflow alınamadı (${res.status})`);
        const items = unwrap(await res.json());
        r = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
        if (!r) throw new Error('Workflow bulunamadı');
      }
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Workflow alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, rowToForm]);

  useEffect(() => { load(); }, [load]);

  // Klonla
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── Saha tanımları ──
  const fetchInputs = useCallback(async (workflowId) => {
    if (!workflowId) { setInputs([]); return; }
    setInputsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${workflowId}/inputs`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Saha tanımları alınamadı (${res.status})`);
      const items = unwrap(await res.json());
      setInputs((Array.isArray(items) ? items : []).map((it) => ({
        id: pick(it, 'id', 'Id'),
        fieldName: pick(it, 'fieldName', 'FieldName') ?? '',
        dataType: pick(it, 'dataType', 'DataType') ?? 1,
        description: pick(it, 'description', 'Description') ?? '',
        defaultValue: pick(it, 'defaultValue', 'DefaultValue') ?? '',
        isRequired: Boolean(pick(it, 'isRequired', 'IsRequired')),
        isActive: pick(it, 'isActive', 'IsActive') ?? true,
      })));
    } catch (err) {
      showToast('error', err.message || 'Saha tanımları alınamadı');
      setInputs([]);
    } finally {
      setInputsLoading(false);
    }
  }, []);

  useEffect(() => { if (!isNew && activeTab === 'inputs') fetchInputs(id); }, [isNew, activeTab, id, fetchInputs]);

  const buildBody = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    isActive: form.isActive === 'true',
  });

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.name.trim()) { showToast('error', 'Workflow adı zorunludur.'); return; }
    setSaving(true);
    try {
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Workflow eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Workflow eklendi.');
        setTimeout(() => navigate('/workflows'), 400);
        return;
      }
      showToast('success', 'Workflow güncellendi.');
      setTimeout(() => navigate('/workflows'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => { navigate('/workflows/new', { state: { clone: { ...form } } }); };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Workflow silindi.');
      setTimeout(() => navigate('/workflows'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // ── Saha modal ──
  const openNewInput = () => { setInputModal({ id: null }); setInputForm(EMPTY_INPUT); };
  const openEditInput = (it) => {
    setInputModal({ id: it.id });
    setInputForm({
      fieldName: it.fieldName || '',
      dataType: String(it.dataType ?? '1'),
      description: it.description || '',
      defaultValue: it.defaultValue || '',
      isRequired: Boolean(it.isRequired),
      isActive: it.isActive ?? true,
    });
  };

  const handleSaveInput = async () => {
    if (isNew) return;
    const fieldName = inputForm.fieldName.trim();
    if (!fieldName) { showToast('error', 'Saha adı zorunludur.'); return; }
    const duplicate = inputs.some((it) => (
      it.id !== inputModal.id && normalizeFieldName(it.fieldName) === normalizeFieldName(fieldName)
    ));
    if (duplicate) { showToast('error', 'Aynı workflow için aynı saha adı tekrar edemez.'); return; }
    setInputSaving(true);
    try {
      // Backend saha güncellemesi sil-ekle ile yapılır (mevcut grid deseni).
      if (inputModal.id) {
        const del = await fetch(`${API_BASE}/${id}/inputs/${inputModal.id}`, { method: 'DELETE', headers: authHeaders() });
        if (!del.ok) throw new Error(`Saha güncellenemedi (${del.status})`);
      }
      const params = new URLSearchParams({
        fieldName,
        dataType: String(parseInt(inputForm.dataType, 10)),
        description: inputForm.description.trim(),
        isRequired: String(Boolean(inputForm.isRequired)),
        isActive: String(Boolean(inputForm.isActive)),
        defaultValue: inputForm.defaultValue.trim(),
      });
      const res = await fetch(`${API_BASE}/${id}/inputs?${params.toString()}`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error(`Saha kaydedilemedi (${res.status})`);
      showToast('success', inputModal.id ? 'Saha güncellendi.' : 'Saha eklendi.');
      setInputModal(null);
      fetchInputs(id);
    } catch (err) {
      showToast('error', err.message || 'Saha kaydı başarısız');
    } finally {
      setInputSaving(false);
    }
  };

  const handleDeleteInput = async (inputId) => {
    try {
      const res = await fetch(`${API_BASE}/${id}/inputs/${inputId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error(`Silme başarısız (${res.status})`);
      showToast('success', 'Saha silindi.');
      fetchInputs(id);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    }
  };

  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'list' },
    { key: 'inputs', label: 'Saha Tanımları', icon: 'layers', disabled: isNew, title: isNew ? 'Önce workflow kaydedin' : undefined },
  ];

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/workflows')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni workflow aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Workflow Yönetimi', to: '/workflows' }, { label: isNew ? 'Yeni Workflow' : 'Workflow Detayı', current: true }]}
      status={statusBadge}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'WorkflowDefinition', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="list" title="Workflow Bilgileri" subtitle="Ad, açıklama ve durum">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="name" label="Workflow Adı" req span placeholder="Workflow adını giriniz" />
              <FieldText form={form} set={set} k="description" label="Açıklama" placeholder="Workflow açıklaması" />
              <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            </Grid>
          </Section>
        </div>
      )}

      {activeTab === 'inputs' && !isNew && (
        <div className="dps-form">
          <Section
            icon="layers"
            title="Saha Tanımları"
            subtitle="Workflow input tanımları"
            right={<button className="dps-blk-add" onClick={openNewInput}><Icon name="plus" size={15} /> Saha Ekle</button>}
          >
            {inputsLoading ? (
              <div className="dps-empty"><span>Yükleniyor…</span></div>
            ) : inputs.length === 0 ? (
              <div className="dps-empty"><Icon name="layers" size={34} /><p>Henüz saha tanımı yok</p><span>Sağ üstten “Saha Ekle” ile ilk kaydı oluşturun.</span></div>
            ) : (
              <table className="dps-table">
                <thead>
                  <tr>
                    <th>Saha Adı</th><th>Veri Tipi</th><th>Varsayılan</th><th>Açıklama</th><th>Zorunlu</th><th>Aktif</th><th className="dps-td-actions">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {inputs.map((it) => (
                    <tr key={it.id}>
                      <td><strong>{it.fieldName}</strong></td>
                      <td>{getDataTypeLabel(it.dataType)}</td>
                      <td>{it.defaultValue || '—'}</td>
                      <td>{it.description || '—'}</td>
                      <td>{it.isRequired ? 'Evet' : 'Hayır'}</td>
                      <td>{it.isActive ? 'Aktif' : 'Pasif'}</td>
                      <td className="dps-td-actions">
                        <button className="dps-row-btn warn" onClick={() => openEditInput(it)}>Düzenle</button>
                        <button className="dps-row-btn danger" onClick={() => handleDeleteInput(it.id)}>Sil</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
      )}

      {/* Saha ekle/düzenle modalı */}
      {inputModal && (
        <SubModal
          title={inputModal.id ? 'Sahayı Düzenle' : 'Yeni Saha'}
          onClose={() => setInputModal(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setInputModal(null)} disabled={inputSaving}>İptal</button>
              <button className="dps-btn primary" onClick={handleSaveInput} disabled={inputSaving}>{inputSaving ? 'Kaydediliyor…' : 'Kaydet'}</button>
            </>
          )}
        >
          <Grid cols={2}>
            <FieldText form={inputForm} set={setInput} tried={inputSaving} k="fieldName" label="Saha Adı" req placeholder="Örn. MusteriAdi" />
            <FieldSelect form={inputForm} set={setInput} k="dataType" label="Veri Tipi" options={DATA_TYPE_OPTIONS} />
            <FieldText form={inputForm} set={setInput} k="defaultValue" label="Varsayılan Değer" placeholder="Opsiyonel" />
            <FieldText form={inputForm} set={setInput} k="description" label="Açıklama" placeholder="Alan amacı" />
          </Grid>
          <Grid cols={2}>
            <FieldSelect
              form={{ isRequired: inputForm.isRequired ? 'true' : 'false' }}
              set={(_, v) => setInput('isRequired', v === 'true')}
              k="isRequired" label="Zorunlu"
              options={[{ value: 'true', label: 'Evet' }, { value: 'false', label: 'Hayır' }]}
            />
            <FieldSelect
              form={{ isActive: inputForm.isActive ? 'true' : 'false' }}
              set={(_, v) => setInput('isActive', v === 'true')}
              k="isActive" label="Aktif"
              options={ACTIVE_OPTIONS}
            />
          </Grid>
        </SubModal>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> workflow kaydını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default WorkflowDetailPage;
